<!-- Component to render the header navigation -->
<template>
	<nav
		class="sticky top-0 z-40 flex flex-0 flex-row justify-between bg-white py-4 print:hidden"
	>
		<!-- Left Side -->
		<div class="ml-5 flex flex-row items-center">
			<RouterLink
				to="/"
				class="flex flex-row"
			>
				<img
					src="/assets/logos/studio_name_mark_blue.svg"
					alt="Sales Studio logo"
					class="w-32"
				/>
				<div
					v-if="environmentName"
					class="mx-2 mt-2 text-xs leading-none! font-bold text-orange-500 uppercase"
				>
					({{ environmentName }})
				</div>
			</RouterLink>
		</div>
		<!-- Right Side -->
		<div class="mr-5 flex flex-row items-center justify-center">
			<SepioProducerWarning
				@click="sepioAPIModalOpen = true"
				v-if="auth.missingSepioGuardApiProducerNumber()"
			/>
			<!-- Sync status -->
			<HeaderNavSyncWithCloudSync
				v-if="useFeatureService().isEnabled(Feature.CloudSync) && sync"
			/>
			<HeaderNavSync
				v-else
				sync
			/>

			<!-- Profile picture -->
			<div class="ml-3 size-9 overflow-hidden rounded-full">
				<img
					:src="profilePhotoUrl"
					class="h-full w-full object-cover"
				/>
			</div>

			<!-- Greetings and log out menu -->
			<div class="group relative flex cursor-pointer items-center">
				<!-- Viewing as user greeting -->
				<div
					v-if="auth.viewingAsAnother"
					class="ml-2 flex text-sm font-medium text-orange-500 group-hover:text-orange-700"
				>
					{{
						t('home.viewingAs', { name: auth.user?.salesContact?.firstname })
					}}
					<PlayIcon
						class="text-primary-500 group-hover:text-primary-700 mt-1 ml-1 size-3 rotate-90"
					/>
				</div>

				<!-- Logged in as self greeting -->
				<div
					v-else
					class="text-primary-500 group-hover:text-primary-700 ml-2 flex text-sm font-medium"
				>
					{{ t('home.hello', { name: auth.user?.hub?.firstname }) }}
					<PlayIcon class="mt-1 ml-1 size-3 rotate-90" />
				</div>

				<!-- Hover menu wrapper to include some blank vertical space -->
				<div
					class="absolute top-full right-0 z-30 hidden w-56.25 group-hover:block"
				>
					<menu
						class="bg-background-50 mt-3 border-separate divide-y divide-slate-200 border-slate-200"
					>
						<MenuItem
							v-if="auth.viewingAsAnother"
							@click="switchModalOpen = true"
						>
							{{ t('auth.switchUser') }}
						</MenuItem>
						<MenuItem @click="inflationModalOpen = true">
							{{ t('home.inflation.inflationSettings') }}
						</MenuItem>
						<MenuItem @click="checkLogOutCondition">
							{{ t('auth.logout') }}
						</MenuItem>
					</menu>
				</div>
			</div>
		</div>
	</nav>

	<!-- Switch user modal -->
	<SwitchUserModal
		v-if="switchModalOpen"
		v-model="switchModalOpen"
		@confirm="switchUser"
	/>

	<!-- Settings modal -->
	<SettingsModal
		v-else-if="inflationModalOpen"
		v-model="inflationModalOpen"
	/>

	<!-- Logout modal -->
	<LogoutModal
		v-else-if="logoutModalOpen"
		v-model="logoutModalOpen"
		:has-unsent-or-failed="hasUnsentOrFailedPacket"
		@confirm="logOut"
	/>

	<!--	Sepio API number missing modal-->
	<SepioProducerWarningModal
		v-else-if="sepioAPIModalOpen"
		v-model="sepioAPIModalOpen"
	/>
</template>

<script setup lang="ts">
import LogoutModal from './LogoutModal.vue'
import MenuItem from './MenuItem.vue'
import SwitchUserModal from './SwitchUserModal.vue'
import DefaultProfile from '@/assets/default-profile.svg'
import HeaderNavSync from '@/components/common/HeaderNavSync.vue'
import HeaderNavSyncWithCloudSync from '@/components/common/HeaderNavSyncWithCloudSync.vue'
import SepioProducerWarning from '@/components/common/SepioProducerWarning.vue'
import SepioProducerWarningModal from '@/components/common/SepioProducerWarningModal.vue'
import SettingsModal from '@/components/common/SettingsModal.vue'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { Feature, useFeatureService } from '@/services/featureService'
import { useAssetStore } from '@/stores/asset'
import { useAuthStore } from '@/stores/auth'
import { useCrmStore } from '@/stores/crm'
import { useSubmissionStore } from '@/stores/submission'
import { PlayIcon } from '@heroicons/vue/24/solid'
import { computedAsync } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const asset = useAssetStore()
const auth = useAuthStore()
const crm = useCrmStore()
const route = useRoute()
const { d, t } = useI18n()

const logoutModalOpen = ref(false)
const sepioAPIModalOpen = ref(false)
const switchModalOpen = ref(false)
const inflationModalOpen = ref(false)

const submissionStore = useSubmissionStore()
const { failedSubmissions } = storeToRefs(submissionStore)

const cloudSyncService = useCloudSyncService()

/** Check if there are any unsent or failed document/packet submissions */
const hasUnsentOrFailedPacket = computed(() => {
	if (!failedSubmissions.value) return false
	return failedSubmissions.value.size > 0
})

defineProps({
	sync: Boolean,
})

/** Get the current environment by hostname and return the appropriate label **/
const environmentName = computed(() => {
	const host = window.location.hostname

	if (host.includes('studio-author')) return 'Author'
	if (host.includes('studio-demo')) return 'Demo'
	if (host.includes('studio-dev')) return 'Dev'
	if (host.includes('studio-stage')) return 'Stage'
	if (host.includes('studio-salesforce')) return 'Salesforce Integration'
	if (host.includes('studio.precoa.com')) return null
	return 'Local'
})

/** Get the user's profile photo via the asset store */
const profilePhotoUrl = computedAsync(async () => {
	const imageId = auth.user?.hub?.profileImageId
	if (!imageId) return DefaultProfile

	return asset.getAssetBlobUrl(
		import.meta.env.VITE_API_ROOT +
			'/v1/images/' +
			imageId +
			'?width=72&height=72'
	)
})

/** Last updated date */
const updatedDate = computed(() => {
	if (!crm.appointments?.downloadedAt) return ''
	let now = new Date()
	let date = new Date(crm.appointments?.downloadedAt)
	if (date.toDateString() === now.toDateString()) {
		return d(date, 'time')
	} else {
		return d(date, 'shortDate')
	}
})

/** Completely log out of the app */
function logOut() {
	auth.logOut()
	logoutModalOpen.value = false
}

/** Stop viewing as another user and bring up the manager/admin screen */
function switchUser() {
	auth.undoViewAppAs()
	switchModalOpen.value = false
}

function checkLogOutCondition() {
	// if CloudSync is on, new logic
	if (useFeatureService().isEnabled(Feature.CloudSync)) {
		// If there is unsent data open modal
		if (
			cloudSyncService.hasPendingCloudSyncs() ||
			hasUnsentOrFailedPacket.value
		) {
			logoutModalOpen.value = true
			return
		} else {
			// if there is no unsent data, log out immediately
			logOut()
		}
	} else {
		// if cloudSync is off, old logic (it always opened the modal)
		logoutModalOpen.value = true
	}
}

/** Auto-open Sepio warning on new session always; on home only once per login/viewAppAs */
function shouldAutoShowSepioProducerWarning() {
	if (!auth.missingSepioGuardApiProducerNumber()) return

	if (route.name === 'newSession') {
		sepioAPIModalOpen.value = true
		return
	}

	// if user is on home dashboard, only auto-show the warning once upon login/altering salesContact
	if (route.name === 'home' && !auth.hasAutoShownSepioProducerWarningOnHome()) {
		sepioAPIModalOpen.value = true
		auth.markSepioProducerWarningShownOnHome()
	}
}

let tabulationTimerId: number
onMounted(async () => {
	shouldAutoShowSepioProducerWarning()
	await cloudSyncService.updateTabulation()
	tabulationTimerId = window.setInterval(
		async () => await cloudSyncService.updateTabulation(),
		5000
	)
})

onUnmounted(() => {
	window.clearInterval(tabulationTimerId)
})
</script>
